import { buildAppPath } from './tenant';

/**
 * Role normalization utilities to ensure consistent role handling across the frontend
 * Matches backend role_utils.py normalization logic
 */

export const ROLES = {
  CLIENT_ADMIN: 'client_admin',
  BRANCH_ADMIN: 'branch_admin',
  LOAN_OFFICER: 'loan_officer',
  LOAN_PROCESSOR: 'loan_processor',
  LEAD_GEN: 'lead_gen',
  READ_ONLY: 'read_only',
  CONSUMER: 'consumer',
};

const ROLE_METADATA = {
  [ROLES.CLIENT_ADMIN]: {
    label: 'Client Admin',
    description: 'Full control of the tenant: manage employees, branches, and reporting.',
    requiresNmls: false,
  },
  [ROLES.BRANCH_ADMIN]: {
    label: 'Branch Admin',
    description: 'Oversees one or more branches. Can invite employees and manage pipeline activity.',
    requiresNmls: true,
  },
  [ROLES.LOAN_OFFICER]: {
    label: 'Loan Officer',
    description: 'Originates new loans and collaborates with processors. Active NMLS is required.',
    requiresNmls: true,
  },
  [ROLES.LOAN_PROCESSOR]: {
    label: 'Loan Processor',
    description: 'Shepherds files through underwriting and closing. NMLS is required for compliance checks.',
    requiresNmls: true,
  },
  [ROLES.LEAD_GEN]: {
    label: 'Lead Gen',
    description: 'Works the loans in one branch: borrower profile and documents only.',
    requiresNmls: false,
  },
  [ROLES.READ_ONLY]: {
    label: 'Read Only',
    description: 'Can view dashboards and pipeline data without editing privileges.',
    requiresNmls: false,
  },
  [ROLES.CONSUMER]: {
    label: 'Consumer',
    description: 'Borrower-facing experience with limited administrative access.',
    requiresNmls: false,
  },
};

// Role aliases mapping to canonical roles
const ROLE_ALIASES = {
  // Primary mappings
  'client_admin': ROLES.CLIENT_ADMIN,
  'branch_admin': ROLES.BRANCH_ADMIN,
  'loan_officer': ROLES.LOAN_OFFICER,
  'loan_processor': ROLES.LOAN_PROCESSOR,
  'lead_gen': ROLES.LEAD_GEN,
  'read_only': ROLES.READ_ONLY,
  'consumer': ROLES.CONSUMER,

  // Stytch member variations
  'stytch_admin': ROLES.CLIENT_ADMIN,
  'stytch_member': ROLES.READ_ONLY,

  // Legacy variations
  'organization': ROLES.CLIENT_ADMIN,
  'org_admin': ROLES.CLIENT_ADMIN,
  'organization_admin': ROLES.CLIENT_ADMIN,
  'company_admin': ROLES.CLIENT_ADMIN,
  'admin': ROLES.CLIENT_ADMIN,
  'platform_admin': ROLES.CLIENT_ADMIN,
  'platform_super_admin': ROLES.CLIENT_ADMIN,
  'superadmin': ROLES.CLIENT_ADMIN,

  // Loan officer variations
  'lo': ROLES.LOAN_OFFICER,
  'loan officer': ROLES.LOAN_OFFICER,
  'loan_officer_admin': ROLES.LOAN_OFFICER,
  'mortgage_officer': ROLES.LOAN_OFFICER,

  // Loan processor variations
  'lp': ROLES.LOAN_PROCESSOR,
  'processor': ROLES.LOAN_PROCESSOR,
  'loan processor': ROLES.LOAN_PROCESSOR,
  'mortgage_processor': ROLES.LOAN_PROCESSOR,

  // Lead gen variations
  'leadgen': ROLES.LEAD_GEN,
  'lead-gen': ROLES.LEAD_GEN,
  'lead gen': ROLES.LEAD_GEN,

  // Branch variations
  'branch manager': ROLES.BRANCH_ADMIN,
  'branch_manager': ROLES.BRANCH_ADMIN,
  'branchadmin': ROLES.BRANCH_ADMIN,

  // Read-only variations
  'viewer': ROLES.READ_ONLY,
  'readonly': ROLES.READ_ONLY,
  'read only': ROLES.READ_ONLY,
  'user': ROLES.READ_ONLY
};

/**
 * Normalize a role string to its canonical form
 * @param {string} role - The role to normalize (case-insensitive)
 * @returns {string} The normalized role or 'read_only' as default
 */
export function normalizeRole(role) {
  if (!role) {
    return ROLES.READ_ONLY;
  }

  // Convert to lowercase and trim
  const normalizedInput = String(role).toLowerCase().trim();

  // Check against our aliases
  const canonicalRole = ROLE_ALIASES[normalizedInput];

  if (!canonicalRole) {
    if (import.meta.env.DEV) {
      console.warn(`[normalizeRole] Unknown role '${role}', defaulting to read_only`);
    }
    return ROLES.READ_ONLY;
  }

  return canonicalRole;
}

export function getRoleMetadata(role) {
  const normalized = normalizeRole(role);
  return ROLE_METADATA[normalized] || {
    label: normalized,
    description: '',
    requiresNmls: false,
  };
}

export function getRoleDescription(role) {
  return getRoleMetadata(role).description;
}

export function roleRequiresNmls(role) {
  return getRoleMetadata(role).requiresNmls;
}

/**
 * Check if a role has admin privileges
 * @param {string} role - The role to check
 * @returns {boolean} True if the role has admin privileges
 */
export function isAdminRole(role) {
  const normalized = normalizeRole(role);
  return normalized === ROLES.CLIENT_ADMIN || normalized === ROLES.BRANCH_ADMIN;
}

/**
 * Check if a role is an employee role
 * @param {string} role - The role to check
 * @returns {boolean} True if the role is an employee role
 */
export function isEmployeeRole(role) {
  const normalized = normalizeRole(role);
  return normalized === ROLES.LOAN_OFFICER || normalized === ROLES.LOAN_PROCESSOR;
}

/**
 * Get the appropriate landing page for a role
 * @param {string} role - The user's role
 * @returns {string} The landing path for that role
 */
export function landingPathForRole(role) {
  const rawInput = String(role || '').toLowerCase();

  if (rawInput.includes('platform')) {
    return buildAppPath('/platform/dashboard');
  }

  const normalized = normalizeRole(role);

  // Admin roles go to dashboard
  if (normalized === ROLES.CLIENT_ADMIN || normalized === ROLES.BRANCH_ADMIN) {
    return buildAppPath('/dashboard');
  }

  // Loan officers go to dashboard
  if (normalized === ROLES.LOAN_OFFICER) {
    return buildAppPath('/dashboard');
  }

  // Loan processors go to pipeline
  if (normalized === ROLES.LOAN_PROCESSOR) {
    return buildAppPath('/pipeline');
  }

  // Lead gen has no dashboard: its work is the Loan Information list
  if (normalized === ROLES.LEAD_GEN) {
    return buildAppPath('/loanInformation');
  }

  // Consumers go to dashboard
  if (normalized === ROLES.CONSUMER) {
    return buildAppPath('/dashboard');
  }

  // Read-only and default go to dashboard
  return buildAppPath('/dashboard');
}

/**
 * Map raw backend roles to legacy frontend role labels used in Redux/UI
 * @param {string} role
 * @returns {string}
 */
export function mapRoleToAppRole(role) {
  const raw = String(role || '').toLowerCase();

  if (raw.includes('platform')) {
    return 'superAdmin';
  }

  switch (normalizeRole(role)) {
    case ROLES.CLIENT_ADMIN:
      return 'organization';
    case ROLES.BRANCH_ADMIN:
      return 'branchAdmin';
    case ROLES.LOAN_OFFICER:
      return 'loanOfficer';
    case ROLES.LOAN_PROCESSOR:
      return 'loanProcessor';
    case ROLES.LEAD_GEN:
      return 'leadGen';
    case ROLES.READ_ONLY:
    default:
      return 'consumer';
  }
}

/**
 * Get display name for a role
 * @param {string} role - The role to get display name for
 * @returns {string} Human-friendly display name
 */
export function getRoleDisplayName(role) {
  const metadata = getRoleMetadata(role);
  return metadata.label || 'Read Only';
}

/**
 * Get role hierarchy level (higher number = more privileges)
 * @param {string} role - The role to check
 * @returns {number} Hierarchy level (0-4)
 */
export function getRoleHierarchy(role) {
  const normalized = normalizeRole(role);

  const hierarchy = {
    [ROLES.CLIENT_ADMIN]: 4,
    [ROLES.BRANCH_ADMIN]: 3,
    [ROLES.LOAN_OFFICER]: 2,
    [ROLES.LOAN_PROCESSOR]: 1,
    [ROLES.LEAD_GEN]: 1,
    [ROLES.READ_ONLY]: 0,
    [ROLES.CONSUMER]: -1
  };

  return hierarchy[normalized] || 0;
}

/**
 * Check if a user role has permission to perform actions on target role
 * @param {string} userRole - The role of the user performing the action
 * @param {string} targetRole - The role being acted upon
 * @returns {boolean} True if user has permission
 */
export function canManageRole(userRole, targetRole) {
  return getRoleHierarchy(userRole) > getRoleHierarchy(targetRole);
}
